const DISMISS_KEY = 'mealPai_install_dismissed_at';
const INSTALLED_KEY = 'mealPai_install_done';
const VISIT_KEY = 'mealPai_visit_count';
const DISMISS_DAYS = 14;

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function isAppInstalled(): boolean {
    if (typeof window === 'undefined') return false;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

export function isIOS(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isMobileBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export function hasCookieConsent(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem('mealPai_cookie_consent') === 'true';
}

export function recordVisit(): number {
    const count = Number(localStorage.getItem(VISIT_KEY) || '0') + 1;
    localStorage.setItem(VISIT_KEY, String(count));
    return count;
}

export function getVisitCount(): number {
    return Number(localStorage.getItem(VISIT_KEY) || '0');
}

export function markInstallDismissed(): void {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

export function markInstallAccepted(): void {
    localStorage.setItem(INSTALLED_KEY, 'true');
    localStorage.removeItem(DISMISS_KEY);
}

export function shouldShowInstallPrompt(): boolean {
    if (typeof window === 'undefined') return false;
    if (isAppInstalled()) return false;
    if (localStorage.getItem(INSTALLED_KEY) === 'true') return false;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || '0');
    if (dismissedAt > 0) {
        const daysSinceDismiss = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < DISMISS_DAYS) return false;
    }

    return isMobileBrowser();
}

export async function registerServiceWorker(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    try {
        await navigator.serviceWorker.register('/sw.js');
    } catch (err) {
        console.warn('Service worker registration failed', err);
    }
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let captureInitialized = false;
type AvailabilityListener = () => void;
const availabilityListeners = new Set<AvailabilityListener>();

function notifyAvailability(): void {
    availabilityListeners.forEach((listener) => listener());
}

export function initInstallPromptCapture(): void {
    if (typeof window === 'undefined' || captureInitialized) return;
    captureInitialized = true;

    window.addEventListener('beforeinstallprompt', (event: Event) => {
        event.preventDefault();
        deferredPrompt = event as BeforeInstallPromptEvent;
        notifyAvailability();
    });

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        markInstallAccepted();
        notifyAvailability();
    });
}

export function canPromptInstall(): boolean {
    return deferredPrompt !== null;
}

export function subscribeInstallAvailability(listener: AvailabilityListener): () => void {
    availabilityListeners.add(listener);
    return () => {
        availabilityListeners.delete(listener);
    };
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export async function triggerInstallPrompt(): Promise<InstallOutcome> {
    const promptEvent = deferredPrompt;
    if (!promptEvent) return 'unavailable';

    try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice.outcome === 'accepted') {
            markInstallAccepted();
        } else {
            markInstallDismissed();
        }
        return choice.outcome;
    } catch (err) {
        console.warn('Install prompt failed', err);
        return 'dismissed';
    } finally {
        deferredPrompt = null;
        notifyAvailability();
    }
}
